/**
 * The always-visible filter summary — the collapsed face of the filter
 * header. Clicking "Filters" (or any chip) EXPANDS the panel below it in
 * place; the chips stay put and keep summarising the state.
 *
 * Shows EVERY declared filter as a chip, including the ones sitting on
 * "All" (Pablo 2026-07-20: "deben salir los filtros actuales, incluso
 * el ALL"). Active chips carry a × that resets that one filter
 * instantly; "All" chips are muted and read-only. A leading
 * "Filters · N" button (N = user overrides) toggles the panel; a
 * trailing "Clear all" (visible only with overrides) resets everything.
 *
 * Pure presentation over the shared filter primitives:
 * ``filterStates`` / ``isFilterActive`` / ``hasUserOverride`` /
 * ``countUserOverrides`` decide the tiers and the badge;
 * ``formatFilterValue`` renders the value; ``resolveFilterLabel``
 * renders the label. It never re-decides what "applied" means.
 */

import { ChevronDown, Filter, Lock, MinusCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  countUserOverrides,
  filterStates,
  hasUserOverride,
  isFilterActive,
} from "./filter-state";
import { groupFacetTags, parseFacetSelection, withFacetAttribute } from "./facet-tags";
import { formatFilterValue, resolveFilterLabel } from "./filter-format";
import { useLang } from "./i18n-binding";
import type { DashboardFilter } from "./types";

export interface FilterChipsProps {
  filters: ReadonlyArray<DashboardFilter>;
  activeFilters: Record<string, string>;
  /** ``new Date()`` — passed in so tests can pin relative-date output. */
  today: Date;
  /** Whether the filter panel below the chips is expanded. */
  expanded: boolean;
  /**
   * Toggle the panel. Called with no arg from the "Filters" trigger
   * (open↔close); called with a filter key from a chip body (always
   * expand — focusing that filter is a follow-up).
   */
  onToggle: (focusKey?: string) => void;
  /** Reset ONE filter to its default (the chip's × — instant). */
  onResetFilter: (filt: DashboardFilter) => void;
  /** Reset every filter to the report defaults ("Clear all"). */
  onClearAll: () => void;
  /** id of the panel the trigger controls, for ``aria-controls``. */
  controlsId?: string;
  /** Dark banner styling (the chips live on the gradient hero). */
  embedded?: boolean;
  /** With ``embedded``: ``"light"`` draws the row on the white
   *  ``ControlPanel`` (transparent row, accent trigger, neutral chips). */
  surface?: "dark" | "light";
  /** The engine's filter options; a ``date_calendar`` entry is its
   *  ``[min, max]`` days, so a ``"latest"`` day reads as the max. */
  filterOptions?: Record<string, string[]>;
  /** Write a filter value. With it, a ``facet_tags`` filter shows one
   *  chip per picked attribute whose × removes that attribute only;
   *  without it, one chip for the whole filter. */
  onFilterChange?: (key: string, value: string) => void;
  /** Report groups (F1 A): group filters with a value the reader set
   *  that the open report does NOT use. Drawn after the live chips,
   *  dashed and struck through, "no aplica en <report>": the value is
   *  never hidden, and it still applies on the reports that use it. */
  notApplicable?: { filters: ReadonlyArray<DashboardFilter>; reportLabel: string } | null;
}

export function FilterChips({
  filters,
  activeFilters,
  today,
  expanded,
  onToggle,
  onResetFilter,
  onClearAll,
  controlsId,
  embedded = true,
  surface = "dark",
  filterOptions,
  onFilterChange,
  notApplicable,
}: FilterChipsProps): JSX.Element | null {
  const light = embedded && surface === "light";
  const dark = embedded && !light;
  const { t } = useTranslation();
  const locale = useLang();
  if (filters.length === 0 && !notApplicable?.filters.length) return null;

  const states = filterStates(filters, activeFilters, today);
  const overrideCount = countUserOverrides(states);
  const byKey = new Map(filters.map((f) => [f.key, f]));

  const ctx = { t, locale };

  return (
    <div
      className={
        dark
          ? "flex flex-wrap items-center gap-1.5 rounded-lg bg-white/[0.06] px-2 py-1.5"
          : light
            ? "flex flex-wrap items-center gap-1.5 py-1"
            : "flex flex-wrap items-center gap-1.5 rounded-lg bg-neutral-100 px-2 py-1.5"
      }
    >
      {/* Trigger — always first, never scrolls away. Toggles the panel. */}
      <button
        type="button"
        onClick={() => onToggle()}
        aria-expanded={expanded}
        aria-controls={controlsId}
        aria-label={t("filters.title")}
        className={
          "inline-flex flex-none items-center gap-1.5 text-xs font-bold transition-colors " +
          (dark
            ? "rounded-md px-2.5 py-1 bg-accent text-white hover:bg-accent-dark"
            : light
              ? // Look v3: the report's pink pill (the tenant accent).
                "rounded-full bg-accent px-3 py-1.5 text-white hover:brightness-95"
              : "rounded-md px-2.5 py-1 bg-neutral-800 text-white hover:bg-neutral-700")
        }
      >
        <Filter size={12} aria-hidden />
        {t("filters.title")}
        {overrideCount > 0 && (
          <span
            className={
              "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-2xs font-bold tabular-nums " +
              (dark || light ? "bg-white text-accent-dark" : "bg-accent text-white")
            }
          >
            {overrideCount}
          </span>
        )}
        <ChevronDown
          size={13}
          aria-hidden
          className={
            "transition-transform duration-200 " + (expanded ? "rotate-180" : "")
          }
        />
      </button>

      {states.map((state) => {
        const filt = byKey.get(state.key)!;
        const active = isFilterActive(state);
        const override = hasUserOverride(state);
        const label = resolveFilterLabel(filt, t, locale);
        const value = active
          ? formatFilterValue(filt, state.effectiveValue, {
              ...ctx,
              latestDay: filterOptions?.[filt.key]?.[1],
            })
          : t("filterbar.all");
        const locked = state.kind === "locked";
        const removable = active && !locked && !filt.required;
        // A facet_tags filter reads like the drawer: one chip per picked
        // attribute ("Panel: QLED"), each × removing that attribute only.
        const parts: Array<{ id: string; label: string; value: string; onRemove: () => void }> =
          filt.facet_tags && active && onFilterChange
            ? groupFacetTags(parseFacetSelection(String(state.effectiveValue ?? ""))).map((g) => ({
                id: `${state.key}:${g.attribute}`,
                label: g.attribute,
                value:
                  g.values.length === 1
                    ? g.values[0]!
                    : t("filterbar.nSelected", { n: g.values.length }),
                onRemove: () =>
                  onFilterChange(
                    filt.key,
                    withFacetAttribute(String(state.effectiveValue ?? ""), g.attribute, []),
                  ),
              }))
            : [{ id: state.key, label, value, onRemove: () => onResetFilter(filt) }];

        // Three tiers, so the row tells you at a glance WHICH filters are
        // narrowing the data: override (you changed it) → accent tint;
        // active on the report default → readable neutral; "All" → muted
        // but still legible. The old "All" at white/45 was near-invisible
        // (Pablo: "poco contraste ... de los filtros sin seleccionar").
        const chipClass = override
          ? dark
            ? "border-accent/50 bg-accent/25 text-white"
            : "border-accent/40 bg-accent/10 text-accent-dark"
          : active
            ? dark
              ? "border-white/25 bg-white/15 text-white"
              : "border-neutral-300 bg-white text-neutral-800"
            : dark
              ? "border-white/15 bg-white/[0.06] text-white/65"
              : "border-neutral-200 bg-white text-neutral-600";
        // Label contrast lifted and the value dropped from bold to medium
        // so the value no longer shouts over its own field name (Pablo:
        // "es raro que el valor seleccionado se vea más que el campo").
        const labelClass = override
          ? dark
            ? "text-accent/15"
            : "text-accent-dark"
          : dark
            ? "text-white/65"
            : "text-neutral-500";

        return parts.map((part) => (
          <span
            key={part.id}
            className={
              "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-2xs transition-colors " +
              chipClass
            }
          >
            {/* The chip body expands the panel (focused on this filter). */}
            <button
              type="button"
              onClick={() => onToggle(state.key)}
              className="inline-flex items-center gap-1 outline-none"
            >
              <span className={labelClass}>{part.label}:</span>
              <span className={active ? "font-medium" : ""}>{part.value}</span>
              {filt.required && <span className="text-accent-dark">*</span>}
              {locked && <Lock size={9} aria-hidden className="opacity-60" />}
            </button>
            {removable && (
              <button
                type="button"
                onClick={part.onRemove}
                aria-label={t("filters.removeChip", { label: part.label })}
                className={
                  "-mr-0.5 rounded-full p-0.5 " +
                  (dark
                    ? override
                      ? "text-accent/15 hover:text-white"
                      : "text-white/60 hover:text-white"
                    : "text-neutral-500 hover:text-neutral-800")
                }
              >
                <X size={11} aria-hidden />
              </button>
            )}
          </span>
        ));
      })}

      {notApplicable?.filters.map((filt) => {
        const label = resolveFilterLabel(filt, t, locale);
        const raw = activeFilters[filt.key] ?? "";
        // A value the reader CLEARED is an override too: it reads "All".
        const value = raw
          ? formatFilterValue(filt, raw, { ...ctx, latestDay: filterOptions?.[filt.key]?.[1] })
          : t("filterbar.all");
        return (
          <span
            key={`na-${filt.key}`}
            data-not-applicable={filt.key}
            className={
              "inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-dashed px-2.5 py-1 text-2xs " +
              (dark
                ? "border-white/30 bg-transparent text-white/60"
                : "border-neutral-300 bg-transparent text-neutral-500")
            }
          >
            <MinusCircle size={11} aria-hidden />
            <span>{label}:</span>
            <span className="line-through">{value}</span>
            <em className="not-italic text-2xs">
              {t("filters.notApplicableIn", { report: notApplicable.reportLabel })}
            </em>
          </span>
        );
      })}

      {/* "Clear all" — resets to the report defaults. Anti-jump: ALWAYS
          in the flow occupying its slot, faded to invisible (and
          non-interactive) when nothing is overridden, so the row never
          reflows when the first override lands or the last one clears. */}
      <button
        type="button"
        onClick={onClearAll}
        aria-hidden={overrideCount === 0 && !filters.some(f => f.depends_on != null)}
        tabIndex={overrideCount === 0 && !filters.some(f => f.depends_on != null) ? -1 : 0}
        className={
          "flex-none text-2xs font-semibold underline underline-offset-2 transition-opacity " +
          (overrideCount === 0 && !filters.some(f => f.depends_on != null) ? "invisible pointer-events-none " : "") +
          (dark ? "text-white/80 hover:text-white" : "text-primary hover:text-primary-ink")
        }
      >
        {t("filters.resetAll")}
      </button>
    </div>
  );
}
